import React, { useRef, useState, useEffect } from 'react'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import RestoreIcon from '@mui/icons-material/Restore'
import { useToast } from '@/shared/contexts/ToastContext'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'

interface AudioPlayerProps {
  audioUrl?: string
  onTabChange?: () => void
  // 1 秒ごとの音の大きさ (0〜100)。渡すとシークバーの上に棒で並べ、静かだった時間を見分けられる
  // (design-book ADR-0074)。無ければ今までどおりシークバーだけ
  loudness?: number[] | null
}

// 音の大きさの推移の高さ (px)。シークバー (8px) の上に、話し声の山と静かな谷が見分けられる最小の高さ
const LOUDNESS_HEIGHT = 28

// 1 秒 1 本の棒を 1 つの path にする (viewBox の横 1 = 1 秒。描くときに幅いっぱいに伸ばす)。
// 2 時間 = 7,200 本でも DOM は 1 要素
export const loudnessPath = (levels: number[]): string => {
  const parts = ['M0,100']
  levels.forEach((level, i) => {
    const y = 100 - Math.max(0, Math.min(100, level))
    parts.push(`L${i},${y}`, `L${i + 1},${y}`)
  })
  parts.push(`L${levels.length},100`, 'Z')
  return parts.join(' ')
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  audioUrl,
  onTabChange,
  loudness,
}) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const audioRef = useRef<HTMLAudioElement>(null)
  const { showToast } = useToast()

  const handlePlayPause = () => {
    if (!audioRef.current) return

    if (isPlaying) {
      audioRef.current.pause()
      setIsPlaying(false)
    } else {
      audioRef.current.play().catch(() => {
        showToast('音声の再生に失敗しました', 'error', 3000)
        setIsPlaying(false)
      })
      setIsPlaying(true)
    }
  }

  const handleReset = () => {
    if (!audioRef.current) return

    audioRef.current.pause()
    audioRef.current.currentTime = 0
    setCurrentTime(0)
    setIsPlaying(false)
  }

  const handleSeek = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !duration) return

    const rect = event.currentTarget.getBoundingClientRect()
    const x = event.clientX - rect.left
    const percentage = x / rect.width
    const newTime = percentage * duration

    audioRef.current.currentTime = newTime
    setCurrentTime(newTime)
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins.toString().padStart(2, '0')}:${secs
      .toString()
      .padStart(2, '0')}`
  }

  useEffect(() => {
    const audio = audioRef.current
    if (!audio || !audioUrl) return

    const updateTime = () => {
      setCurrentTime(audio.currentTime)
    }

    const updateDuration = () => {
      if (audio.duration && !isNaN(audio.duration)) {
        setDuration(audio.duration)
      }
    }

    const handleEnded = () => {
      setIsPlaying(false)
    }

    const handlePlay = () => {
      setIsPlaying(true)
    }

    const handlePause = () => {
      setIsPlaying(false)
    }

    audio.addEventListener('timeupdate', updateTime)
    audio.addEventListener('loadedmetadata', updateDuration)
    audio.addEventListener('durationchange', updateDuration)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('play', handlePlay)
    audio.addEventListener('pause', handlePause)

    if (audio.readyState >= 1) {
      updateDuration()
    }

    return () => {
      audio.removeEventListener('timeupdate', updateTime)
      audio.removeEventListener('loadedmetadata', updateDuration)
      audio.removeEventListener('durationchange', updateDuration)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('play', handlePlay)
      audio.removeEventListener('pause', handlePause)
    }
  }, [audioUrl])

  // 音声URLが変更された際の状態リセット（key prop pattern 推奨）
  // 親コンポーネントで audioUrl を key として使用することで、
  // 新しい音声が読み込まれた際にコンポーネントを再マウントすることを推奨
  // ここでは後方互換性のため、audioUrl の変化を派生状態として扱う
  const [lastAudioUrl, setLastAudioUrl] = useState(audioUrl)
  if (audioUrl !== lastAudioUrl) {
    setLastAudioUrl(audioUrl)
    // レンダリング中に直接 ref にアクセスせず、状態のみリセット
    // audio 要素の src 属性が変わることで自然にリセットされる
    setCurrentTime(0)
    setDuration(0)
    setIsPlaying(false)
  }

  // タブ切り替え時の停止処理
  // onTabChange の変更を検出（外部イベント）
  const [lastOnTabChange, setLastOnTabChange] = useState(onTabChange)
  if (onTabChange !== lastOnTabChange) {
    setLastOnTabChange(onTabChange)
    // 停止処理は次のレンダリング後に実行されるようスケジュール
    if (onTabChange && isPlaying) {
      setIsPlaying(false)
    }
  }

  // isPlaying が false になった際に音声を停止
  // これは外部システム（audio 要素）との同期処理
  useEffect(() => {
    if (!isPlaying && audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause()
    }
  }, [isPlaying])

  if (!audioUrl) return null

  const hasLoudness = Boolean(loudness && loudness.length > 0)

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Tooltip title={isPlaying ? '一時停止' : '再生'}>
          <IconButton
            onClick={handlePlayPause}
            sx={{
              color: colorTokens.fg.muted,
              '&:hover': {
                backgroundColor: 'rgba(117, 117, 117, 0.08)',
              },
            }}
          >
            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
        </Tooltip>
        <Tooltip title="最初から再生">
          <IconButton
            onClick={handleReset}
            sx={{
              color: colorTokens.fg.muted,
              '&:hover': {
                backgroundColor: 'rgba(117, 117, 117, 0.08)',
              },
            }}
          >
            <RestoreIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="body2" sx={{ ml: 2 }}>
          {formatTime(currentTime)} / {formatTime(duration)}
        </Typography>
      </Box>
      {hasLoudness && (
        // 音の大きさの推移。シークバーと同じ幅・同じ時間軸で、押した位置へ seek する (バーと同じ手)。
        // 色は罫線と同じ 1 色 (border.default)。静かだった時間は棒が無い = 平らに見える
        <Box
          role="img"
          aria-label="音の大きさの推移"
          onClick={handleSeek}
          sx={{
            height: LOUDNESS_HEIGHT,
            mb: 0.5,
            cursor: 'pointer',
            color: colorTokens.border.default,
          }}
        >
          <svg
            width="100%"
            height="100%"
            viewBox={`0 0 ${loudness!.length} 100`}
            preserveAspectRatio="none"
            style={{ display: 'block' }}
          >
            <path d={loudnessPath(loudness!)} fill="currentColor" />
          </svg>
        </Box>
      )}
      <Box
        sx={{
          position: 'relative',
          height: 8,
          backgroundColor: colorTokens.border.default,
          borderRadius: `${RADIUS.full}px`,
          cursor: 'pointer',
          '&:hover': {
            '& .progress-bar': {
              backgroundColor: colorTokens.accent.primaryHover,
            },
          },
        }}
        onClick={handleSeek}
      >
        <Box
          className="progress-bar"
          sx={[
            {
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              backgroundColor: colorTokens.fg.muted,
              borderRadius: `${RADIUS.full}px`,
              transition: 'background-color 0.2s',
            },
            duration > 0
              ? {
                  width: `${(currentTime / duration) * 100}%`,
                }
              : {
                  width: '0%',
                },
          ]}
        />
        <Box
          sx={[
            {
              position: 'absolute',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: 12,
              height: 12,
              backgroundColor: colorTokens.fg.muted,
              borderRadius: '50%',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
              opacity: 0,
              transition: 'opacity 0.2s',
              '.MuiBox-root:hover &': {
                opacity: 1,
              },
            },
            duration > 0
              ? {
                  left: `${(currentTime / duration) * 100}%`,
                }
              : {
                  left: '0%',
                },
          ]}
        />
      </Box>
      <audio
        ref={audioRef}
        src={audioUrl}
        crossOrigin="use-credentials"
        preload="metadata"
        onLoadedMetadata={(e) => {
          const audio = e.currentTarget
          if (audio.duration && !isNaN(audio.duration)) {
            setDuration(audio.duration)
          }
        }}
      />
    </Box>
  )
}

export default AudioPlayer
